import { ClinicAuthError } from "@auth/clinicAuthClient";
import { useUser } from "@context/UserContext";
import type { TaskViewRef } from "@tasks/tasksClient";
import { canSign } from "@users/userClient";
import { useSignView } from "@views/viewsQueries";

/**
 * The sign-off gate for a `sign` task (BE-2034), shared by every presentation of
 * one. Signing acts on the view (`POST /views/{id}/sign`), never `PATCH /tasks`,
 * and is deliberately non-optimistic: nothing here flips the card, the refetch
 * `useSignView` fires does (a 409 refetches too, so someone else's signature
 * lands the same way).
 *
 * The role check is a courtesy; the server's is the real gate, and its 403
 * surfaces through the global write-failure notice via `useSignView`'s
 * `meta.action`, so no caller renders failure UI of its own.
 */
export const useTaskSigning = (
  view: TaskViewRef | undefined,
  /** What the server ruled on the signature: `true` if it landed, `false` if it
      did not. Callers that show a signature landing (`useSignCeremony`) must key
      off this rather than the task leaving the list, which Decline also does. */
  onSettled?: (signed: boolean) => void,
) => {
  const { currentUser } = useUser();
  const signView = useSignView();

  const isSigner = canSign(currentUser);
  const alreadySigned = view?.status === "signed";

  return {
    /** DVMs and practice managers; everyone else reads the card as context. */
    isSigner,
    alreadySigned,
    /** Whether to offer the Sign/Decline pair at all. */
    canAct: !alreadySigned && isSigner,
    isPending: signView.isPending,
    sign: () => {
      if (!view) return;
      signView.mutate(view.id, {
        onSuccess: () => onSettled?.(true),
        // A 409 is someone else's signature landing, so the view is signed
        // either way; every other failure leaves it unsigned.
        onError: (error) =>
          onSettled?.(error instanceof ClinicAuthError && error.status === 409),
      });
    },
  };
};
